import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { InputRef } from 'antd';
import { Dispatch } from 'redux';
import { TireSeasonSelection } from 'components/TireSeasonSelection/TireSeasonSelection';
import { toSearchParams } from 'domains/tires/Tire.mapper';
import {
  getFulltextSearchData,
  getTireSearchQuery,
  getTireSetParam,
  setFulltextSearchData,
  setSearchParams,
  setTireSearchQuery,
  setTireSetParam,
  tireProductsSearchRequestSaga,
  tiresFulltextSearchRequestSaga,
} from 'domains/tires/Tire.store';
import { TireSet } from 'domains/tires/Tire.types';
import { TireBrandSelection } from 'pages/TiresPage/TireSearch/TireBrandSelection';
import { TireSearchProps } from 'pages/TiresPage/TireSearch/TireSearch';
import TireSetSelection from 'pages/TiresPage/TireSearch/TireSetSelection';
import { QueryWorkUtils } from 'pages/TiresPage/TireSearch/TiresSearchbar/QueryWorkUtils';
import { TiresFulltextSearchResults } from 'pages/TiresPage/TireSearch/TiresSearchbar/TiresFulltextSearchResults';
import { BlackButton, Box, Flex, Input, MarginBox, notifyTop } from 'UI';
import { useFocusOnMount, useLarge } from 'utils';
import { useOutsideClick } from 'utils/hooks/useOutsideClick';

const whiteSpaceRegex = new RegExp(/\s{1,2}$/);

const setQuery = (queryValue: string, dispatch: Dispatch) => {
  dispatch(setTireSearchQuery({ query: queryValue }));
};

export const TiresSearchbar = ({ brands, setBrands, season, setSeason }: TireSearchProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const large = useLarge();
  const query = useSelector(getTireSearchQuery);
  const [menuOpen, setMenuOpen] = useState(false);
  const [queryDebounced, setQueryDebounced] = useState('');
  const currentStoreTireSet = useSelector(getTireSetParam);
  const [tireSet, setTireSet] = useState<TireSet | undefined>(currentStoreTireSet);
  const fulltextSearchData = useSelector(getFulltextSearchData).data;
  const searchTyresRef = useRef<InputRef>(null);
  const [isPasted, setIsPasted] = useState(false);

  useFocusOnMount<InputRef>(searchTyresRef);

  const closeFulltextSearchResults = useCallback(() => {
    if (fulltextSearchData?.length) {
      dispatch(setFulltextSearchData([]));
    }
  }, [dispatch, fulltextSearchData]);

  const ref = useOutsideClick<HTMLDivElement>(closeFulltextSearchResults);

  function fulltextSearch(val: string) {
    const trimQuery = query.trim();
    const trimVal = val.trim();
    if (trimVal.length > 2 && trimQuery === trimVal) {
      dispatch(tiresFulltextSearchRequestSaga({ fulltextSearch: trimVal }));
      setQueryDebounced(trimVal);
    }
  }

  const handleSearch = useCallback(
    (searchValue: string) => {
      dispatch(setTireSetParam(tireSet));
      const selected = brands.filter((b) => b.selected);
      const newSearchParams = toSearchParams(
        searchValue,
        season,
        selected.map((brand) => brand.brandId),
        '/',
      );
      if (selected) {
        dispatch(
          tireProductsSearchRequestSaga({
            query: searchValue,
            season,
            brands: selected.map((brand) => brand.brandId),
            chargeIndicatorSeparator: '/',
          }),
        );
        dispatch(
          setSearchParams({
            searchParamsBase64: newSearchParams.searchParamsBase64,
          }),
        );
      }
      setQuery(newSearchParams.tireSearchParams.replaceAll('-', ' '), dispatch);
      closeFulltextSearchResults();
    },
    [dispatch, season, brands, closeFulltextSearchResults, tireSet],
  );

  const handleKeyPress = useCallback(
    (event: { key: string }) => {
      if (event.key === 'Enter') {
        if (query.split(' ').length < 4) {
          return;
        }
        handleSearch(query);
      }
    },
    [query, handleSearch],
  );

  return (
    <Flex justify={'center'} minWidth={large ? 1000 : 900} gap={30}>
      <Flex align={'center'} size={0} minWidth={190} direction={'row'} justify={'space-between'}>
        <Box width={190}>
          <MarginBox mx={10}>
            <TireSeasonSelection
              season={season}
              setSeason={(newSeason) => setSeason(newSeason)}
              menuOpen={menuOpen}
              setMenuOpen={(isOpen) => setMenuOpen(isOpen)}
              translateX={0}
              translateY={6}
              bordered
              showValue
              mainSearch
              data-cy={'tires-season-button'}
            />
          </MarginBox>
        </Box>
      </Flex>
      <Flex align={'center'} size={'0 1 150px'} minWidth={190}>
        <TireBrandSelection
          brands={brands}
          setBrands={setBrands}
          translateY={6}
          mainSearch
          data-cy={'tires-brand-button'}
        />
      </Flex>
      <Flex align={'center'} size={'0 1 150px'} minWidth={190}>
        <TireSetSelection tireSet={tireSet} setTireSet={setTireSet} data-cy={'tires-set-button'} />
      </Flex>
      <Flex align={'center'} minWidth={250}>
        <Box width={'100%'} ref={ref} background={'white'}>
          <Input
            bordered
            size={'large'}
            value={query}
            onChange={(val) => {
              const tirePattern = /^\s*(\d{3})\s*(\d{2})\s*(?:R\s*)?(\d{2})\s*(\d{2})([A-Z])?\s*$/i;
              const match = tirePattern.exec(val.replace(/\s+/g, ''));
              const normalizedVal = match
                ? [match[1], match[2], match[3], match[4], match[5]?.toUpperCase()].filter(Boolean).join(' ')
                : val;
              const trimVal =
                normalizedVal.length <= 12 && normalizedVal.length > query.length && whiteSpaceRegex.test(normalizedVal)
                  ? normalizedVal.slice(0, -1)
                  : normalizedVal;
              const arrQueries = trimVal.split(' ');
              const isValid = QueryWorkUtils.checkCorrectInput(arrQueries, isPasted);
              setIsPasted(false);
              if (!isValid) {
                notifyTop('error', t('catalog.tires.search.error', 'No result. Please type a valid tire size'));
                return;
              }
              const addSpace = QueryWorkUtils.addEndSpace(arrQueries, isPasted);
              if (trimVal.length > query.length) {
                setQuery(arrQueries.join(' ') + (addSpace ? ' ' : ''), dispatch);
              } else {
                setQuery(arrQueries.join(' ').substring(0, trimVal.length - (addSpace ? 1 : 0)), dispatch);
              }
            }}
            onPaste={() => setIsPasted(true)}
            onChangeDebounced={fulltextSearch}
            onKeyPress={handleKeyPress}
            passRef={searchTyresRef}
            placeholder={t('catalog.tires.search.placeholder', 'Reference or Tire size')}
            data-cy={'tire-search'}
          />
          <TiresFulltextSearchResults search={queryDebounced} onClickOnSelectedValue={handleSearch} />
        </Box>
      </Flex>
      <BlackButton
        disabled={query.split(' ').length < 4}
        onClick={() => handleSearch(query)}
        size={'xlarge'}
        data-cy={'search-button'}
      >
        <Flex minWidth={122} justify={'center'}>
          {t('catalog.tires.search', 'Search')}
        </Flex>
      </BlackButton>
    </Flex>
  );
};
